import React, { useState, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "./ui/dialog";
import { ArrowRight, Calendar, Users, Mail, Phone, MapPin, Target, UtensilsCrossed, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { EMAIL, PHONE_TEL } from "../lib/utils";

const FORFAITS = [
  { value: "100", label: "100 billes — 22 €/pers." },
  { value: "200", label: "200 billes — 27 €/pers." },
  { value: "300", label: "300 billes — 32 €/pers." },
  { value: "500", label: "500 billes — 37 €/pers." },
  { value: "junior", label: "Tarif Junior — 5 €/partie/joueur" },
  { value: "a-definir", label: "À définir avec l'équipe" },
];

const SITES = [
  { value: "fougeres", label: "Fougères (20 Bd Nelson Mandela, 35300)" },
  { value: "louvigne-du-desert", label: "Louvigné-du-Désert (La Morinais, 35420)" },
];

const initialState = {
  nom: "",
  prenom: "",
  telephone: "",
  email: "",
  date: "",
  participants: "",
  forfait: "",
  site: "",
  food: "non",
  urgent: "non",
  info: "",
};

export default function PreReservationDialog({ trigger, defaultSite = "" }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ...initialState, site: defaultSite });
  const [copiedKey, setCopiedKey] = useState(null);

  const change = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const copyToClipboard = async (text, key, label) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for older browsers / non-HTTPS
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopiedKey(key);
      toast.success(`${label} copié !`);
      setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 2000);
    } catch (err) {
      toast.error("Impossible de copier — sélectionnez le texte manuellement.");
    }
  };

  const siteLabel = useMemo(
    () => SITES.find((s) => s.value === form.site)?.label?.split(" (")[0] ?? "À définir",
    [form.site]
  );
  const forfaitLabel = useMemo(
    () => FORFAITS.find((f) => f.value === form.forfait)?.label ?? "À définir",
    [form.forfait]
  );

  const { subject, body, mailto } = useMemo(() => {
    const subj = `Pré-réservation Paintball — ${siteLabel} — ${
      form.date || "date à définir"
    } — ${form.participants || "?"} participants`;
    const bod = `Bonjour,

Je souhaite effectuer une pré-réservation pour une session de paintball à Paintball Aventures 35.

— MES COORDONNÉES —
Nom / Prénom    : ${form.nom} ${form.prenom}
Téléphone       : ${form.telephone}
Email           : ${form.email}

— MA SESSION —
Date souhaitée         : ${form.date || "À définir"}
Nombre de participants : ${form.participants || "?"}
Forfait choisi         : ${forfaitLabel}
Site choisi            : ${siteLabel}
Nourriture & boissons  : ${form.food === "oui" ? "Oui (galettes-saucisses + boissons)" : "Non"}
Session dans les 48 h  : ${form.urgent === "oui" ? "Oui — urgent" : "Non"}

— INFORMATIONS COMPLÉMENTAIRES —
${form.info || "(aucune)"}

Dans l'attente de votre retour, je reste disponible pour tout échange.

Cordialement,
${form.prenom} ${form.nom}`;
    return {
      subject: subj,
      body: bod,
      mailto: `mailto:${EMAIL}?subject=${encodeURIComponent(subj)}&body=${encodeURIComponent(bod)}`,
    };
  }, [form, siteLabel, forfaitLabel]);

  const isValid =
    form.nom && form.prenom && form.telephone && form.email && form.date && form.participants && form.forfait && form.site;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        data-testid="pre-reservation-dialog"
        className="max-w-2xl w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto p-0 border-2 border-black rounded-none gap-0 bg-white"
      >
        {/* Header */}
        <div className="bg-[#E91E63] text-white p-6 md:p-8 sticky top-0 z-10">
          <DialogHeader className="text-left">
            <p className="font-mono text-[10px] md:text-xs font-bold uppercase tracking-[0.3em] text-[#FFEB3B] mb-2">
              Formulaire de pré-réservation
            </p>
            <DialogTitle className="font-display text-3xl md:text-4xl font-bold uppercase leading-none tracking-tight text-white">
              Réservez votre<br />session paintball
            </DialogTitle>
            <DialogDescription className="text-white/90 mt-3 text-sm">
              Remplissez ce formulaire — votre client mail s&apos;ouvrira pré-rempli pour envoyer la demande à l&apos;équipe.
            </DialogDescription>
          </DialogHeader>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!isValid) return;
            window.location.href = mailto;
          }}
          className="p-6 md:p-8 space-y-7"
        >
          {/* Coordonnées */}
          <section>
            <p className="font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-[#E91E63] mb-3">
              01 — Vos coordonnées
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Nom" required>
                <input
                  type="text"
                  required
                  value={form.nom}
                  onChange={change("nom")}
                  data-testid="prr-nom"
                  className={inputCls}
                />
              </Field>
              <Field label="Prénom" required>
                <input
                  type="text"
                  required
                  value={form.prenom}
                  onChange={change("prenom")}
                  data-testid="prr-prenom"
                  className={inputCls}
                />
              </Field>
              <Field label="Téléphone" required icon={Phone}>
                <input
                  type="tel"
                  required
                  value={form.telephone}
                  onChange={change("telephone")}
                  placeholder="06 12 34 56 78"
                  data-testid="prr-telephone"
                  className={inputCls}
                />
              </Field>
              <Field label="Email" required icon={Mail}>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={change("email")}
                  placeholder="vous@email.fr"
                  data-testid="prr-email"
                  className={inputCls}
                />
              </Field>
            </div>
          </section>

          {/* Session */}
          <section>
            <p className="font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-[#E91E63] mb-3">
              02 — Votre session
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Date souhaitée" required icon={Calendar}>
                <input
                  type="date"
                  required
                  value={form.date}
                  onChange={change("date")}
                  data-testid="prr-date"
                  className={inputCls}
                />
              </Field>
              <Field label="Nombre de participants" required icon={Users}>
                <input
                  type="number"
                  min="1"
                  required
                  value={form.participants}
                  onChange={change("participants")}
                  placeholder="ex : 8"
                  data-testid="prr-participants"
                  className={inputCls}
                />
              </Field>
            </div>
          </section>

          {/* Forfait */}
          <section>
            <p className="font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-[#E91E63] mb-3">
              <Target className="inline w-3 h-3 mr-1 mb-0.5" strokeWidth={3} />
              03 — Forfait souhaité
            </p>
            <div className="grid sm:grid-cols-2 gap-2">
              {FORFAITS.map((f) => (
                <label
                  key={f.value}
                  data-testid={`prr-forfait-${f.value}`}
                  className={`cursor-pointer border-2 px-4 py-3 text-sm font-semibold transition-all ${
                    form.forfait === f.value
                      ? "border-[#E91E63] bg-[#fce4ec] text-[#E91E63]"
                      : "border-gray-200 bg-white hover:border-black"
                  }`}
                >
                  <input
                    type="radio"
                    name="forfait"
                    value={f.value}
                    checked={form.forfait === f.value}
                    onChange={change("forfait")}
                    className="sr-only"
                    required
                  />
                  {f.label}
                </label>
              ))}
            </div>
          </section>

          {/* Site */}
          <section>
            <p className="font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-[#E91E63] mb-3">
              <MapPin className="inline w-3 h-3 mr-1 mb-0.5" strokeWidth={3} />
              04 — Site choisi
            </p>
            <div className="grid sm:grid-cols-2 gap-2">
              {SITES.map((s) => (
                <label
                  key={s.value}
                  data-testid={`prr-site-${s.value}`}
                  className={`cursor-pointer border-2 px-4 py-3 text-sm font-semibold transition-all ${
                    form.site === s.value
                      ? "border-[#E91E63] bg-[#fce4ec] text-[#E91E63]"
                      : "border-gray-200 bg-white hover:border-black"
                  }`}
                >
                  <input
                    type="radio"
                    name="site"
                    value={s.value}
                    checked={form.site === s.value}
                    onChange={change("site")}
                    className="sr-only"
                    required
                  />
                  {s.label}
                </label>
              ))}
            </div>
          </section>

          {/* Food option */}
          <section>
            <p className="font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-[#E91E63] mb-3">
              <UtensilsCrossed className="inline w-3 h-3 mr-1 mb-0.5" strokeWidth={3} />
              05 — Nourriture &amp; Boissons (option)
            </p>
            <p className="text-xs text-gray-600 mb-3">
              En option : galettes-saucisses et boissons disponibles sur réservation uniquement (à prévoir avant la séance).
            </p>
            <div className="grid grid-cols-2 gap-2">
              {[
                { v: "oui", label: "Oui, j'en veux" },
                { v: "non", label: "Non merci" },
              ].map((o) => (
                <label
                  key={o.v}
                  data-testid={`prr-food-${o.v}`}
                  className={`cursor-pointer border-2 px-4 py-3 text-sm font-semibold transition-all ${
                    form.food === o.v
                      ? "border-[#E91E63] bg-[#fce4ec] text-[#E91E63]"
                      : "border-gray-200 bg-white hover:border-black"
                  }`}
                >
                  <input
                    type="radio"
                    name="food"
                    value={o.v}
                    checked={form.food === o.v}
                    onChange={change("food")}
                    className="sr-only"
                  />
                  {o.label}
                </label>
              ))}
            </div>
          </section>

          {/* Urgent 48h */}
          <section>
            <p className="font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-[#E91E63] mb-3">
              06 — Souhaitez-vous jouer dans les 48 h ?
            </p>
            <div className="grid grid-cols-2 gap-2">
              {[
                { v: "oui", label: "⚡ Oui, dans les 48 h" },
                { v: "non", label: "Non" },
              ].map((o) => (
                <label
                  key={o.v}
                  data-testid={`prr-urgent-${o.v}`}
                  className={`cursor-pointer border-2 px-4 py-3 text-sm font-semibold transition-all ${
                    form.urgent === o.v
                      ? "border-[#E91E63] bg-[#fce4ec] text-[#E91E63]"
                      : "border-gray-200 bg-white hover:border-black"
                  }`}
                >
                  <input
                    type="radio"
                    name="urgent"
                    value={o.v}
                    checked={form.urgent === o.v}
                    onChange={change("urgent")}
                    className="sr-only"
                  />
                  {o.label}
                </label>
              ))}
            </div>
            {form.urgent === "oui" && (
              <div
                data-testid="prr-urgent-cta"
                className="mt-3 border-2 border-[#E91E63] bg-[#fff5f7] p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3"
              >
                <p className="text-sm text-gray-800 flex-1">
                  <strong>⚡ Votre aventure commence maintenant !</strong>
                  <br />
                  Pas de temps à perdre — appelez-nous pour trouver un créneau aujourd&apos;hui ou demain.
                </p>
                <a
                  href={`tel:${PHONE_TEL}`}
                  data-testid="prr-urgent-call"
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#E91E63] text-white font-bold uppercase text-xs tracking-wider hover:-translate-y-0.5 transition-transform whitespace-nowrap"
                >
                  <Phone className="w-4 h-4" strokeWidth={2.5} />
                  06 68 22 01 60
                </a>
              </div>
            )}
          </section>

          {/* Info */}
          <section>
            <p className="font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-[#E91E63] mb-3">
              07 — Informations complémentaires (optionnel)
            </p>
            <textarea
              value={form.info}
              onChange={change("info")}
              data-testid="prr-info"
              placeholder="Type d'événement (EVG, EVJF, anniversaire, CE…), questions, demandes spécifiques…"
              rows={4}
              className={`${inputCls} resize-none`}
            />
          </section>

          {/* Fallback copy section */}
          <section
            data-testid="prr-copy-fallback"
            className="border-2 border-dashed border-[#E91E63] bg-[#fff5f7] p-4 md:p-5"
          >
            <p className="font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-[#E91E63] mb-2">
              Le lien d&apos;envoi ne s&apos;ouvre pas ?
            </p>
            <p className="text-sm text-gray-800 mb-4">
              Pas de panique — copiez le message ci-dessous et collez-le dans votre boîte mail (Gmail, Outlook, Yahoo…) puis envoyez-le à&nbsp;:
            </p>

            {/* Recipient */}
            <CopyRow
              label="Destinataire"
              value={EMAIL}
              testId="prr-copy-email"
              copied={copiedKey === "email"}
              onCopy={() => copyToClipboard(EMAIL, "email", "Email")}
            />

            {/* Subject */}
            <CopyRow
              label="Objet"
              value={subject}
              testId="prr-copy-subject"
              copied={copiedKey === "subject"}
              onCopy={() => copyToClipboard(subject, "subject", "Objet")}
              compact
            />

            {/* Body preview */}
            <div className="mt-3">
              <div className="flex items-center justify-between mb-1.5 gap-3">
                <span className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-gray-600">
                  Message
                </span>
                <button
                  type="button"
                  data-testid="prr-copy-body"
                  onClick={() => copyToClipboard(body, "body", "Message")}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 border-2 border-black text-[10px] font-bold uppercase tracking-wider hover:bg-black hover:text-white transition-colors"
                >
                  {copiedKey === "body" ? (
                    <>
                      <Check className="w-3 h-3" strokeWidth={3} />
                      Copié
                    </>
                  ) : (
                    <>
                      <Copy className="w-3 h-3" strokeWidth={3} />
                      Copier
                    </>
                  )}
                </button>
              </div>
              <textarea
                readOnly
                value={body}
                data-testid="prr-body-preview"
                rows={6}
                onFocus={(e) => e.target.select()}
                className="w-full px-3 py-2.5 border-2 border-gray-200 bg-white text-xs font-mono text-gray-800 resize-none focus:border-[#E91E63] focus:outline-none whitespace-pre"
              />
            </div>

            <button
              type="button"
              data-testid="prr-copy-all"
              onClick={() =>
                copyToClipboard(
                  `À : ${EMAIL}\nObjet : ${subject}\n\n${body}`,
                  "all",
                  "Message complet"
                )
              }
              className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-black text-white font-bold uppercase text-xs tracking-wider hover:bg-[#E91E63] transition-colors"
            >
              {copiedKey === "all" ? (
                <>
                  <Check className="w-4 h-4" strokeWidth={3} />
                  Message complet copié !
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" strokeWidth={3} />
                  Tout copier (destinataire + objet + message)
                </>
              )}
            </button>
          </section>

          {/* Submit */}
          <div className="pt-2 sticky bottom-0 bg-white -mx-6 md:-mx-8 px-6 md:px-8 py-4 border-t-2 border-black flex flex-col sm:flex-row gap-3">
            <a
              href={isValid ? mailto : "#"}
              onClick={(e) => {
                if (!isValid) e.preventDefault();
              }}
              data-testid="prr-submit"
              className={`flex-1 inline-flex items-center justify-center gap-2 px-6 py-4 font-bold uppercase text-sm tracking-wider transition-all ${
                isValid
                  ? "bg-[#E91E63] text-white hover:-translate-y-0.5 hover:shadow-[0_8px_0_-2px_#0a0a0a]"
                  : "bg-gray-200 text-gray-400 cursor-not-allowed"
              }`}
            >
              Envoyer ma pré-réservation
              <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
            </a>
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="prr-cancel"
              className="px-6 py-4 border-2 border-black text-black font-bold uppercase text-sm tracking-wider hover:bg-black hover:text-white transition-colors"
            >
              Annuler
            </button>
          </div>
          {!isValid && (
            <p className="text-xs text-gray-500 -mt-3">
              Remplissez tous les champs obligatoires pour activer l&apos;envoi.
            </p>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
}

const inputCls =
  "w-full px-4 py-3 border-2 border-gray-200 bg-white text-sm font-body focus:border-[#E91E63] focus:outline-none transition-colors";

function Field({ label, required, icon: Icon, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5 flex items-center gap-1.5">
        {Icon && <Icon className="w-3.5 h-3.5 text-[#E91E63]" strokeWidth={2.5} />}
        {label}
        {required && <span className="text-[#E91E63]">*</span>}
      </span>
      {children}
    </label>
  );
}

function CopyRow({ label, value, testId, copied, onCopy, compact = false }) {
  return (
    <div className={compact ? "mt-2" : "mt-2 first:mt-0"}>
      <div className="flex items-center justify-between mb-1.5 gap-3">
        <span className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-gray-600">
          {label}
        </span>
        <button
          type="button"
          data-testid={testId}
          onClick={onCopy}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 border-2 border-black text-[10px] font-bold uppercase tracking-wider hover:bg-black hover:text-white transition-colors"
        >
          {copied ? (
            <>
              <Check className="w-3 h-3" strokeWidth={3} />
              Copié
            </>
          ) : (
            <>
              <Copy className="w-3 h-3" strokeWidth={3} />
              Copier
            </>
          )}
        </button>
      </div>
      <input
        readOnly
        value={value}
        onFocus={(e) => e.target.select()}
        className="w-full px-3 py-2.5 border-2 border-gray-200 bg-white text-xs font-mono text-gray-800 focus:border-[#E91E63] focus:outline-none"
      />
    </div>
  );
}
